import React, {useState} from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Container,
    Divider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {useUserAuth} from "../../context/UserAuthContext";
import {updateAssociation} from "../../services/associationService";
import PortalTopbar from "./PortalTopbar";

const PRIMARY = "#3E4F88";

/**
 * Perfil da associação — espelha o ProfilePage + EditProfilePage da app:
 * vista dos dados + edição de nome, NIF, morada, telefone, LinkedIn e
 * outras informações. O registo completo é atualizado (mantém Id, Email,
 * ProfileURL, Enabled, RegisterDate, etc.), igual à app.
 */
const ProfilePage = () => {

    const {profile, refreshProfile} = useUserAuth();

    const [editing, setEditing] = useState(false);
    const [name, setName] = useState("");
    const [nif, setNif] = useState("");
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [linkedin, setLinkedin] = useState("");
    const [info, setInfo] = useState("");

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
        if (!profile) return;
        setName(profile.Name || "");
        setNif(profile.NIF || "");
        setAddress(profile.Address || "");
        setPhone(profile.PhoneNumber || "");
        setLinkedin(profile.Linkedin || "");
        setInfo(profile.OtherInfo || "");
        setError("");
        setSuccess("");
        setEditing(true);
    };

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!name.trim()) {
            setError("Indica o nome da associação.");
            return;
        }
        if (!nif.trim()) {
            setError("Indica o NIF da associação.");
            return;
        }

        setSaving(true);
        try {
            // Atualiza o registo completo, preservando os campos de gestão
            // (mesma abordagem do EditProfilePage da app)
            const record = {
                ...profile,
                Name: name.trim(),
                NIF: nif.trim(),
                Address: address.trim(),
                PhoneNumber: phone.trim(),
                Linkedin: linkedin.trim(),
                OtherInfo: info.trim(),
            };
            await updateAssociation(profile.key, record);
            await refreshProfile();
            setEditing(false);
            setSuccess("Dados atualizados com sucesso.");
        } catch (err) {
            console.error("Erro ao atualizar perfil:", err);
            setError("Erro ao atualizar os dados. Tenta novamente.");
        } finally {
            setSaving(false);
        }
    };

    if (!profile) {
        return (
            <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
                <PortalTopbar/>
                <Container maxWidth="sm" sx={{py: 8}}>
                    <Alert severity="warning">
                        Não foi possível carregar o perfil da associação.
                    </Alert>
                </Container>
            </Box>
        );
    }

    const detail = (label, value) => (
        <Box sx={{minWidth: 160}}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 0.25}}>
                {label}
            </Typography>
            <Typography variant="body1">{value || "—"}</Typography>
        </Box>
    );

    return (
        <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
            <PortalTopbar/>
            <Container maxWidth="md" sx={{py: 5}}>
                <Stack spacing={3}>
                    <Box sx={{bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 2, p: 4}}>
                        {success && <Alert severity="success" sx={{mb: 2}}>{success}</Alert>}
                        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

                        <Stack direction={{xs: "column", sm: "row"}} spacing={3} alignItems="center">
                            <Avatar
                                src={profile.ProfileURL || undefined}
                                sx={{width: 110, height: 110, border: 2, borderColor: PRIMARY}}
                            >
                                🏛️
                            </Avatar>
                            <Box sx={{flex: 1}}>
                                <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY}}>
                                    {profile.Name}
                                </Typography>
                                <Typography color="text.secondary">
                                    {profile.Email}
                                </Typography>
                                {profile.RegisterDate && (
                                    <Typography variant="body2" color="text.disabled" sx={{mt: 0.5}}>
                                        Membro desde {profile.RegisterDate}
                                    </Typography>
                                )}
                            </Box>
                            {!editing && (
                                <Button
                                    variant="outlined"
                                    sx={{color: PRIMARY, borderColor: PRIMARY, fontWeight: 600}}
                                    onClick={startEdit}
                                >
                                    Editar perfil
                                </Button>
                            )}
                        </Stack>

                        <Divider sx={{my: 3}}/>

                        {!editing ? (
                            <>
                                <Box sx={{display: "grid", gap: 2, gridTemplateColumns: {xs: "1fr", sm: "repeat(2, 1fr)"}}}>
                                    {detail("NIF", profile.NIF)}
                                    {detail("Telefone", profile.PhoneNumber)}
                                    {detail("Morada", profile.Address)}
                                    {detail("LinkedIn", profile.Linkedin)}
                                </Box>
                                {profile.OtherInfo && (
                                    <>
                                        <Divider sx={{my: 2}}/>
                                        {detail("Outras informações", profile.OtherInfo)}
                                    </>
                                )}
                            </>
                        ) : (
                            <form onSubmit={save} noValidate>
                                <Stack spacing={2.5}>
                                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                        <TextField
                                            label="Nome da associação"
                                            size="small"
                                            required
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            sx={{flex: 2}}
                                        />
                                        <TextField
                                            label="NIF"
                                            size="small"
                                            required
                                            value={nif}
                                            onChange={(e) => setNif(e.target.value)}
                                            sx={{flex: 1}}
                                        />
                                    </Stack>
                                    <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                                        <TextField
                                            label="Morada"
                                            size="small"
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            sx={{flex: 2}}
                                        />
                                        <TextField
                                            label="Telefone"
                                            size="small"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            sx={{flex: 1}}
                                        />
                                    </Stack>
                                    <TextField
                                        label="LinkedIn"
                                        size="small"
                                        value={linkedin}
                                        onChange={(e) => setLinkedin(e.target.value)}
                                    />
                                    <TextField
                                        label="Outras informações"
                                        size="small"
                                        multiline
                                        minRows={3}
                                        value={info}
                                        onChange={(e) => setInfo(e.target.value)}
                                    />
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={saving}
                                            sx={{bgcolor: PRIMARY, fontWeight: 600}}
                                        >
                                            {saving ? "A guardar…" : "Guardar alterações"}
                                        </Button>
                                        <Button
                                            variant="text"
                                            onClick={() => {
                                                setEditing(false);
                                                setError("");
                                            }}
                                        >
                                            Cancelar
                                        </Button>
                                    </Stack>
                                </Stack>
                            </form>
                        )}
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default ProfilePage;